/**
 * Plan explain — dev-only trace that captures every intermediate decision
 * made by buildCoachPlan so the /practice/explain page can surface them.
 *
 * All functions are pure. Tree-shaker removes this from production bundles
 * because it is only imported inside import.meta.env.DEV branches.
 */

import { type Item, type CurriculumNode } from "./item-library.js";
import {
  unlockedNodes,
  getEligibleNewNodes,
  type CardStore,
  type MasteryThresholds,
} from "./scheduler.js";
import { type ReviewSummary } from "./skill-state.js";
import {
  type PlanTemplate,
  type PlanBuildData,
  type SessionMix,
  ITEMS_PER_MINUTE,
} from "./plan-template.js";
import { resolveTemplate } from "./performance-analyzer.js";
import { isDue, currentRetrievability } from "./fsrs.js";
import { ACQUISITION_THRESHOLDS, DEFAULT_ACQUISITION_THRESHOLD } from "./acquisition.js";
import { buildCoachPlan, type CoachPlan } from "./coach-plan.js";

// ─── trace types ──────────────────────────────────────────────────────────────

export interface FilterNodeResult {
  nodeId: string;
  title: string;
  /** Items in template.nodes whose prerequisites are mastered/practicing. */
  passedA: boolean;
  /** ≥ minNodeEngagement non-new cards. */
  passedB: boolean;
  /** ≥ minPracticeReviewsPerNode practice-sourced reviews. */
  passedC: boolean;
  nonNewCardCount: number;
  practiceReviewCount: number;
  bypassedA: boolean;
  bypassedB: boolean;
  bypassedC: boolean;
}

export interface InterventionNodeStat {
  nodeId: string;
  title: string;
  attempts: number;
  lapses: number;
  lapseRate: number;
  triggered: boolean;
}

export interface StrengthenNodeStat {
  nodeId: string;
  title: string;
  recentAttempts: number;
  recentLapses: number;
  lapseRate: number;
  isWinner: boolean;
}

export interface SelectedItemInfo {
  itemId: string;
  nodeId: string;
  nodeTitle: string;
  retrievability: number;
  dueDate: string | null;
}

export interface StrengthenTrace {
  mode: "high-lapse" | "weakest-items" | "omitted";
  lapseWindowDays: number;
  lapseThreshold: number;
  lapseMinCount: number;
  warmupItemCap: number;
  relistenThreshold: number;
  nodeStats: StrengthenNodeStat[];
  winnerNodeId: string | null;
  winnerNodeTitle: string | null;
  winnerLapseRate: number;
  winnerLapseCount: number;
  relistenNudge: boolean;
  selectedItems: SelectedItemInfo[];
}

export interface ReviewTrace {
  budget: number;
  budgetFormula: string;
  reviewBudgetFraction: number;
  schedulableItemCount: number;
  dueCountTotal: number;
  dueCountSelected: number;
  topNodeBreakdown: { nodeId: string; title: string; count: number }[];
  selectedItems: SelectedItemInfo[];
}

export interface IntroduceNodeAcquisition {
  nodeId: string;
  title: string;
  exposures: number;
  threshold: number;
  status: "needs-listen" | "ready";
}

export interface IntroduceTrace {
  skippedReviewOnly: boolean;
  eligibleNodes: { nodeId: string; title: string }[];
  pinnedNodeId: string | null;
  primaryNodeId: string | null;
  primaryNodeTitle: string | null;
  secondNodeId: string | null;
  secondNodeTitle: string | null;
  alreadyUsedCount: number;
  remainingMinutes: number;
  dynamicCapFormula: string;
  dynamicCap: number;
  newItemCap: number;
  nodeAcquisition: IntroduceNodeAcquisition[];
  selectedItemCount: number;
  listenFirst: boolean;
}

export interface CoachTrace {
  templateId: string;
  templateLabel: string;
  resolvedTemplateId: string;
  resolvedTemplateLabel: string;
  sessionTargetMinutes: number;
  sessionMix: SessionMix;
  totalItemCount: number;
  totalCardCount: number;
  cardsByState: Record<string, number>;
  filterBypasses: { A: boolean; B: boolean; C: boolean };
  filterThresholds: { minNodeEngagement: number; minPracticeReviewsPerNode: number };
  nodeFilterResults: FilterNodeResult[];
  interventionThresholds: { lapseThreshold: number; minAttempts: number; windowDays: number };
  interventionStats: InterventionNodeStat[];
  mode: "standard" | "intervention";
  interventionNodeTitle: string | undefined;
  strengthenTrace: StrengthenTrace;
  reviewTrace: ReviewTrace;
  introduceTrace: IntroduceTrace;
  finalStops: { kind: string; itemCount: number; estimatedMinutes: number }[];
}

// ─── buildCoachPlanWithTrace ──────────────────────────────────────────────────

/**
 * Same inputs as buildCoachPlan. Returns the real plan plus a full trace of
 * every intermediate decision. Suitable only for dev/debug tooling.
 */
export function buildCoachPlanWithTrace(
  template: PlanTemplate,
  input: {
    items: Item[];
    cards: CardStore;
    thresholds: MasteryThresholds;
    reviews: readonly ReviewSummary[];
    sessionTargetMinutes: number;
    sessionMix: SessionMix;
    newSkillNodeId?: string | null;
    nodeExposures?: ReadonlyMap<string, number>;
    now?: Date;
  },
): { plan: CoachPlan; trace: CoachTrace } {
  const now = input.now ?? new Date();
  const newSkillNodeId = input.newSkillNodeId ?? null;
  const nodeExposures = input.nodeExposures ?? new Map<string, number>();

  // Run the real plan first — preserves shuffle in strengthenStop.
  const plan = buildCoachPlan(template, { ...input, now, newSkillNodeId, nodeExposures });

  // ── reconstruct filter sets (identical logic to buildCoachPlan) ─────────────

  const bypassA = template.config.bypassFilterA ?? false;
  const bypassB = template.config.bypassFilterB ?? false;
  const bypassC = template.config.bypassFilterC ?? false;

  const unlockedNodeSet: ReadonlySet<string> = bypassA
    ? new Set(template.nodes.map((n) => n.id))
    : new Set(
        unlockedNodes(template.nodes, input.items, input.cards, {
          thresholds: input.thresholds,
        }).map((n) => n.id),
      );

  const engagedNodeSet: ReadonlySet<string> = bypassB
    ? new Set(template.nodes.map((n) => n.id))
    : new Set(
        template.nodes
          .filter(
            (n) =>
              input.items.filter(
                (i) =>
                  i.nodeId === n.id &&
                  input.cards[i.id] !== undefined &&
                  input.cards[i.id]!.state !== "new",
              ).length >= template.config.minNodeEngagement,
          )
          .map((n) => n.id),
      );

  const practiceReviewedNodeSet: ReadonlySet<string> = bypassC
    ? new Set(template.nodes.map((n) => n.id))
    : new Set(
        template.nodes
          .filter((n) => {
            const nodeItemIds = new Set(
              input.items.filter((i) => i.nodeId === n.id).map((i) => i.id),
            );
            let count = 0;
            for (const r of input.reviews) {
              if (!nodeItemIds.has(r.itemId)) continue;
              if (r.source === "practice") count++;
              if (count >= template.config.minPracticeReviewsPerNode) break;
            }
            return count >= template.config.minPracticeReviewsPerNode;
          })
          .map((n) => n.id),
      );

  // ── per-node filter results ─────────────────────────────────────────────────

  const nodeFilterResults: FilterNodeResult[] = template.nodes.map((node) => {
    const nonNewCardCount = input.items.filter(
      (i) =>
        i.nodeId === node.id &&
        input.cards[i.id] !== undefined &&
        input.cards[i.id]!.state !== "new",
    ).length;
    const nodeItemIds = new Set(input.items.filter((i) => i.nodeId === node.id).map((i) => i.id));
    let practiceReviewCount = 0;
    for (const r of input.reviews) {
      if (nodeItemIds.has(r.itemId) && r.source === "practice") practiceReviewCount++;
    }
    return {
      nodeId: node.id,
      title: node.title,
      passedA: unlockedNodeSet.has(node.id),
      passedB: engagedNodeSet.has(node.id),
      passedC: practiceReviewedNodeSet.has(node.id),
      nonNewCardCount,
      practiceReviewCount,
      bypassedA: bypassA,
      bypassedB: bypassB,
      bypassedC: bypassC,
    };
  });

  // ── intervention analysis ───────────────────────────────────────────────────

  const data: PlanBuildData = {
    items: input.items,
    cards: input.cards,
    thresholds: input.thresholds,
    reviews: input.reviews,
    sessionTargetMinutes: input.sessionTargetMinutes,
    sessionMix: input.sessionMix,
    newSkillNodeId,
    nodeExposures,
    forceAcquired: false,
    now,
    nodes: template.nodes,
    unlockedNodeSet,
    engagedNodeSet,
    practiceReviewedNodeSet,
  };

  const { resolvedTemplate, mode, interventionNodeTitle } = resolveTemplate(template, data);
  const config = resolvedTemplate.config;

  const interventionCutoff = new Date(now.getTime() - config.lapseWindowMs);
  const interventionStats: InterventionNodeStat[] = template.nodes
    .filter((n) => unlockedNodeSet.has(n.id) && engagedNodeSet.has(n.id))
    .map((node) => {
      const nodeItemIds = new Set(
        input.items.filter((i) => i.nodeId === node.id).map((i) => i.id),
      );
      const recent = input.reviews.filter(
        (r) => nodeItemIds.has(r.itemId) && new Date(r.timestamp) >= interventionCutoff,
      );
      const lapses = recent.filter((r) => !r.correct).length;
      const lapseRate = recent.length > 0 ? lapses / recent.length : 0;
      return {
        nodeId: node.id,
        title: node.title,
        attempts: recent.length,
        lapses,
        lapseRate,
        triggered: mode === "intervention" && interventionNodeTitle === node.title,
      };
    });

  // ── card inventory ──────────────────────────────────────────────────────────

  const cardsByState: Record<string, number> = { new: 0, learning: 0, review: 0, relearning: 0 };
  for (const card of Object.values(input.cards)) {
    if (card) cardsByState[card.state] = (cardsByState[card.state] ?? 0) + 1;
  }
  // Items without any card record count as "new".
  const neverSeenCount = input.items.filter((i) => !input.cards[i.id]).length;
  cardsByState["new"] = (cardsByState["new"] ?? 0) + neverSeenCount;

  // ── helper ──────────────────────────────────────────────────────────────────

  const nodeTitleFor = (nodeId: string): string =>
    template.nodes.find((n) => n.id === nodeId)?.title ?? nodeId;

  const toItemInfo = (item: Item): SelectedItemInfo => {
    const card = input.cards[item.id];
    return {
      itemId: item.id,
      nodeId: item.nodeId,
      nodeTitle: nodeTitleFor(item.nodeId),
      retrievability: card ? currentRetrievability(card, now) : 0,
      dueDate: card?.due ?? null,
    };
  };

  // ── strengthen trace ────────────────────────────────────────────────────────

  const strengthenStopResult = plan.stops.find((s) => s.kind === "strengthen");

  const schedulableItems = input.items.filter(
    (i) =>
      unlockedNodeSet.has(i.nodeId) &&
      engagedNodeSet.has(i.nodeId) &&
      practiceReviewedNodeSet.has(i.nodeId),
  );

  const lapseWindowCutoff = new Date(now.getTime() - config.lapseWindowMs);
  let winnerNodeId: string | null = null;
  let winnerNodeTitle: string | null = null;
  let winnerLapseRate = 0;
  let winnerLapseCount = 0;

  const strengthenNodeStats: StrengthenNodeStat[] = [];
  for (const node of template.nodes) {
    const nodeItemIds = new Set(
      schedulableItems.filter((i) => i.nodeId === node.id).map((i) => i.id),
    );
    if (nodeItemIds.size === 0) continue;
    const recent = input.reviews.filter(
      (r) => nodeItemIds.has(r.itemId) && new Date(r.timestamp) >= lapseWindowCutoff,
    );
    if (recent.length === 0) continue;
    const lapses = recent.filter((r) => !r.correct).length;
    const rate = lapses / recent.length;
    if (
      rate >= config.warmupLapseThreshold &&
      lapses >= config.warmupLapseMinCount &&
      rate > winnerLapseRate
    ) {
      winnerNodeId = node.id;
      winnerNodeTitle = node.title;
      winnerLapseRate = rate;
      winnerLapseCount = lapses;
    }
    strengthenNodeStats.push({
      nodeId: node.id,
      title: node.title,
      recentAttempts: recent.length,
      recentLapses: lapses,
      lapseRate: rate,
      isWinner: false,
    });
  }
  for (const stat of strengthenNodeStats) {
    if (stat.nodeId === winnerNodeId) stat.isWinner = true;
  }

  const strengthenMode: StrengthenTrace["mode"] = !strengthenStopResult
    ? "omitted"
    : winnerNodeId
    ? "high-lapse"
    : "weakest-items";

  const strengthenTrace: StrengthenTrace = {
    mode: strengthenMode,
    lapseWindowDays: config.lapseWindowMs / (24 * 60 * 60 * 1000),
    lapseThreshold: config.warmupLapseThreshold,
    lapseMinCount: config.warmupLapseMinCount,
    warmupItemCap: config.warmupItemCap,
    relistenThreshold: config.relistenLapseThreshold,
    nodeStats: strengthenNodeStats,
    winnerNodeId,
    winnerNodeTitle,
    winnerLapseRate,
    winnerLapseCount,
    relistenNudge: winnerLapseRate >= config.relistenLapseThreshold,
    selectedItems: (strengthenStopResult?.items ?? []).map(toItemInfo),
  };

  // ── review trace ────────────────────────────────────────────────────────────

  const reviewStopResult = plan.stops.find((s) => s.kind === "review");
  const alreadyInStrengthen = new Set((strengthenStopResult?.items ?? []).map((i) => i.id));

  const budget = Math.round(
    input.sessionTargetMinutes * ITEMS_PER_MINUTE * config.reviewBudgetFraction,
  );
  const budgetFormula = `${input.sessionTargetMinutes} min × ${ITEMS_PER_MINUTE} items/min × ${config.reviewBudgetFraction} = ${budget}`;

  const allDue = schedulableItems.filter((i) => {
    if (alreadyInStrengthen.has(i.id)) return false;
    const c = input.cards[i.id];
    return c !== undefined && c.state !== "new" && isDue(c, now);
  });

  const dueNodeCount = new Map<string, number>();
  for (const item of allDue) {
    dueNodeCount.set(item.nodeId, (dueNodeCount.get(item.nodeId) ?? 0) + 1);
  }
  const topNodeBreakdown = [...dueNodeCount.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([nodeId, count]) => ({ nodeId, title: nodeTitleFor(nodeId), count }));

  const reviewTrace: ReviewTrace = {
    budget,
    budgetFormula,
    reviewBudgetFraction: config.reviewBudgetFraction,
    schedulableItemCount: schedulableItems.length,
    dueCountTotal: allDue.length,
    dueCountSelected: reviewStopResult?.items.length ?? 0,
    topNodeBreakdown,
    selectedItems: (reviewStopResult?.items ?? []).map(toItemInfo),
  };

  // ── introduce trace ─────────────────────────────────────────────────────────

  const introduceStopResult = plan.stops.find((s) => s.kind === "introduce");
  const alreadyUsedCount =
    (strengthenStopResult?.items.length ?? 0) + (reviewStopResult?.items.length ?? 0);
  const remainingMinutes = Math.max(
    0,
    input.sessionTargetMinutes - alreadyUsedCount / ITEMS_PER_MINUTE,
  );
  const dynamicCap = Math.max(
    config.newItemCap,
    Math.floor(remainingMinutes * ITEMS_PER_MINUTE * 0.9),
  );
  const dynamicCapFormula = `max(${config.newItemCap}, floor(${remainingMinutes.toFixed(1)} min × ${ITEMS_PER_MINUTE} × 0.9)) = max(${config.newItemCap}, ${Math.floor(remainingMinutes * ITEMS_PER_MINUTE * 0.9)}) = ${dynamicCap}`;

  const eligibleNodes = getEligibleNewNodes(template.nodes, input.items, input.cards);
  const pinnedNode = newSkillNodeId
    ? eligibleNodes.find((n) => n.id === newSkillNodeId) ?? null
    : null;
  const primaryNode = pinnedNode ?? eligibleNodes[0] ?? null;
  const secondNode = !pinnedNode && eligibleNodes.length > 1 ? eligibleNodes[1] : undefined;

  const acquisitionNodes: CurriculumNode[] = [
    ...(primaryNode ? [primaryNode] : []),
    ...(secondNode ? [secondNode] : []),
  ];

  const nodeAcquisition: IntroduceNodeAcquisition[] = acquisitionNodes.map((node) => {
    const exposures = nodeExposures.get(node.id) ?? 0;
    const threshold = ACQUISITION_THRESHOLDS[node.id] ?? DEFAULT_ACQUISITION_THRESHOLD;
    const hasExistingPractice = input.items.some(
      (i) => i.nodeId === node.id && (input.cards[i.id]?.reps ?? 0) > 0,
    );
    const status: "needs-listen" | "ready" =
      hasExistingPractice || exposures >= threshold ? "ready" : "needs-listen";
    return { nodeId: node.id, title: node.title, exposures, threshold, status };
  });

  const introduceTrace: IntroduceTrace = {
    skippedReviewOnly: input.sessionMix === "review-only",
    eligibleNodes: eligibleNodes.map((n) => ({ nodeId: n.id, title: n.title })),
    pinnedNodeId: newSkillNodeId,
    primaryNodeId: primaryNode?.id ?? null,
    primaryNodeTitle: primaryNode?.title ?? null,
    secondNodeId: secondNode?.id ?? null,
    secondNodeTitle: secondNode?.title ?? null,
    alreadyUsedCount,
    remainingMinutes,
    dynamicCapFormula,
    dynamicCap,
    newItemCap: config.newItemCap,
    nodeAcquisition,
    selectedItemCount: introduceStopResult?.items.length ?? 0,
    listenFirst: introduceStopResult?.listenFirst ?? false,
  };

  // ── assemble ────────────────────────────────────────────────────────────────

  const trace: CoachTrace = {
    templateId: template.id,
    templateLabel: template.label,
    resolvedTemplateId: resolvedTemplate.id,
    resolvedTemplateLabel: resolvedTemplate.label,
    sessionTargetMinutes: input.sessionTargetMinutes,
    sessionMix: input.sessionMix,
    totalItemCount: input.items.length,
    totalCardCount: Object.keys(input.cards).length,
    cardsByState,
    filterBypasses: { A: bypassA, B: bypassB, C: bypassC },
    filterThresholds: {
      minNodeEngagement: template.config.minNodeEngagement,
      minPracticeReviewsPerNode: template.config.minPracticeReviewsPerNode,
    },
    nodeFilterResults,
    interventionThresholds: {
      lapseThreshold: config.interventionLapseThreshold,
      minAttempts: config.interventionMinAttempts,
      windowDays: config.lapseWindowMs / (24 * 60 * 60 * 1000),
    },
    interventionStats,
    mode,
    interventionNodeTitle,
    strengthenTrace,
    reviewTrace,
    introduceTrace,
    finalStops: plan.stops.map((s) => ({
      kind: s.kind,
      itemCount: s.itemCount,
      estimatedMinutes: s.estimatedMinutes,
    })),
  };

  return { plan, trace };
}
